import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/events"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Events
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Privacy Policy</h1>
        </div>

        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <strong>Last updated:</strong> {new Date().toLocaleDateString()}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Introduction</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This Privacy Policy describes how the Blood on the Clocktower Event Signup Application ("we," "our," or "us") 
              collects, uses, and protects your personal information when you use our event management platform.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              By using our application, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Information We Collect</h2>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                🔒 No Direct Personal Information Collection
              </h3>
              <p className="text-blue-800 dark:text-blue-200">
                We do not directly capture or collect personal information from users. Any personally identifiable information (PII) 
                stored in our system is voluntarily provided by users through their own actions, such as event signups, notes, or comments.
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-3">Information Provided by Users</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300">
              <li>Discord username and display name (via Discord OAuth)</li>
              <li>Discord user ID and avatar (via Discord OAuth)</li>
              <li>Event signup preferences and status (user-initiated)</li>
              <li>Notes and comments you choose to add to event signups (user-provided)</li>
              <li>Event creation details (if you create events)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-3">Usage Information</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300">
              <li>Event creation and management activities</li>
              <li>Signup and cancellation history</li>
              <li>Application usage patterns and preferences</li>
              <li>Technical information (browser type, device information)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>To provide and maintain our event management services</li>
              <li>To process your event signups and manage event participation</li>
              <li>To communicate with you about events and updates</li>
              <li>To improve our application and user experience</li>
              <li>To ensure the security and integrity of our platform</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Information Sharing</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, 
              except in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>With Discord (for authentication and user management)</li>
              <li>With event organizers and administrators (for event management)</li>
              <li>When required by law or to protect our rights and safety</li>
              <li>With service providers who assist in operating our platform (under strict confidentiality agreements)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Data Security</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We implement appropriate security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication through Discord OAuth</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and user permissions</li>
              <li>Secure hosting and infrastructure</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Your Rights</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
            </ul>
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-green-800 dark:text-green-200 text-sm">
                <strong>User Control:</strong> Since all PII in our system is user-provided, you have complete control over 
                what personal information is stored. You can modify or remove your information at any time through the application.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Data Retention</h2>
            <p className="text-gray-700 dark:text-gray-300">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. 
              Event data is typically retained for historical purposes, but you can request deletion of your personal information 
              at any time through your profile settings or by contacting us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our application integrates with the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li><strong>Discord:</strong> For user authentication and profile information</li>
              <li><strong>Firebase:</strong> For front-end hosting and deployment</li>
              <li><strong>Neon:</strong> For database storage and management</li>
              <li><strong>Render:</strong> For backend server processing</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              All data is stored in the United States. These services have their own privacy policies, 
              and we encourage you to review them.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                <strong>Data Location:</strong> All user data and application information is stored on servers 
                located in the United States, regardless of where you access the application from.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Our application is not intended for children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If you are a parent or guardian and believe your child has provided 
              us with personal information, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
              new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this 
              Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Contact Us</h2>
            <p className="text-gray-700 dark:text-gray-300">
              If you have any questions about this Privacy Policy or our data practices, please contact us through 
              the Discord server or through the application's support channels.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 